import { createEffect, createSignal, For, type JSX, on, onCleanup, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import type { AcceptedMoves } from '../lib/accepted-move';
import { undoShown, undoText } from '../lib/move-note';
import type { MovePage } from '../lib/move-page';
import { transferHead, transferProgress, transferTag, transferWhy } from '../lib/transfer-copy';
import type { TransferCard as Card, CardPlace, TransferState } from '../lib/transfer-stack';

// One transfer card (#6773, prototype mvdPush / mvdDraw `:3127-3225`): an icon tile (the
// only thing that carries the state's colour), the head and its mono tag, the name, the route,
// then three steps or a held / failed sentence, "Running on <to>" and the note's Undo on a
// complete card, a Dismiss button and a 2 px progress bar drawn by `scaleX`. NO live role: the
// page already announces each acceptance and each refusal once (`MovePage.announce`).
//
// A held or failed card shakes once (340 ms, from 420 ms), never under reduced motion; the
// animation is cancelled on unmount and its rejected `finished` is observed.

const STEP_KEYS = [
  'fleet.transfer.step_checkpoint',
  'fleet.transfer.step_transfer',
  'fleet.transfer.step_resume',
] as const;

const SHAKE: Keyframe[] = [
  { transform: 'translateX(0)' },
  { transform: 'translateX(-5px)' },
  { transform: 'translateX(5px)' },
  { transform: 'translateX(-2px)' },
  { transform: 'translateX(0)' },
];

export type TransferCardProps = {
  readonly card: Card;
  readonly state: () => TransferState;
  readonly place: () => CardPlace | undefined;
  readonly page: Pick<
    MovePage,
    'boxOf' | 'blockedWhy' | 'clock' | 'announce' | 'locked' | 'textOf'
  >;
  readonly accepted: Pick<AcceptedMoves, 'note' | 'newestBatchId' | 'undo' | 'undoing'>;
  readonly reduced: boolean;
  readonly leaving?: boolean;
  /** The card's natural height, measured: the layout fans and peeks by it. */
  readonly onMeasure?: (px: number) => void;
  /** Dismiss pressed; `from` is the button, so focus can move on from it. */
  readonly onDismiss: (from: HTMLElement) => void;
  /** A click on a card peeking behind the front one. */
  readonly onBehind: () => void;
};

function Icon(props: { readonly state: TransferState }): JSX.Element {
  const common = {
    width: '18',
    height: '18',
    viewBox: '0 0 20 20',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '2',
    'stroke-linecap': 'round',
    'stroke-linejoin': 'round',
  } as const;
  return (
    <span
      class="transfer-card__icon"
      classList={{ 'transfer-card__icon--live': props.state.kind === 'moving' }}
      aria-hidden="true"
    >
      <Show when={props.state.kind === 'moving'}>
        <svg {...common} class="transfer-card__spin" aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" opacity="0.22" />
          <path d="M10 2.5a7.5 7.5 0 0 1 7.5 7.5" />
        </svg>
      </Show>
      <Show when={props.state.kind === 'complete'}>
        <svg {...common} aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" opacity="0.3" />
          <path d="M6.6 10.2l2.3 2.3 4.6-4.9" />
        </svg>
      </Show>
      <Show when={props.state.kind === 'failed'}>
        <svg {...common} aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" opacity="0.3" />
          <path d="M7.4 7.4l5.2 5.2M12.6 7.4l-5.2 5.2" />
        </svg>
      </Show>
      <Show when={props.state.kind === 'held'}>
        <svg {...common} aria-hidden="true">
          <circle cx="10" cy="10" r="7.5" opacity="0.3" />
          <path d="M10 6v4.2l2.8 1.8" />
        </svg>
      </Show>
    </span>
  );
}

/** The complete card's action row: "Running on <to>" and the note's Undo (option A). */
function CardActions(props: TransferCardProps): JSX.Element {
  const nameOf = (id: string): string => props.page.boxOf(id)?.display_name ?? id.slice(0, 8);
  // The note's Undo, only while the note names THIS card's batch and Undo is offered.
  const note = () => {
    const o = props.card.origin;
    if (props.state().kind !== 'complete' || props.card.putBack || o.kind !== 'accepted') {
      return null;
    }
    const n = props.accepted.note();
    if (n === null || props.accepted.newestBatchId() !== o.ref.batchId) return null;
    return undoShown(n.undo) ? n : null;
  };
  const running = (): boolean => {
    const s = props.state();
    return s.kind === 'complete' && s.running && props.card.toId !== null;
  };
  const blocked = (): string | null => {
    const n = note();
    return n === null ? null : props.page.blockedWhy(n.undoFrom);
  };
  const live = (): boolean =>
    note()?.undo.kind === 'live' && blocked() === null && !props.accepted.undoing();
  const press = (): void => {
    if (note() === null) props.page.announce(t('fleet.move.why_undo_unavailable'));
    else if (live()) props.accepted.undo();
    else if (props.page.locked() > 0) props.page.announce(t('fleet.machines.ctl_model_locked'));
  };
  return (
    <Show when={running() || note() !== null}>
      <div class="transfer-card__act">
        <span>
          {running() ? t('fleet.transfer.running_on', { name: nameOf(props.card.toId ?? '') }) : ''}
        </span>
        <Show when={note()}>
          {(n) => (
            <button
              type="button"
              class="transfer-card__undo"
              aria-disabled={live() ? undefined : 'true'}
              title={blocked() ?? t('fleet.move.undo_title', { name: nameOf(n().undoTo) })}
              onClick={press}
            >
              {undoText(n().undo, props.page.clock(), t)}
            </button>
          )}
        </Show>
      </div>
    </Show>
  );
}

export function TransferCard(props: TransferCardProps): JSX.Element {
  let el: HTMLElement | undefined;
  const [entering, setEntering] = createSignal(!props.reduced && props.leaving !== true);
  // A leaving card mounts where it last sat, then slides 44 px right and fades on the next frame.
  const [gone, setGone] = createSignal(false);
  let frame = 0;
  let shake: Animation | null = null;
  onMount(() => {
    if (entering()) frame = requestAnimationFrame(() => setEntering(false));
    else if (props.leaving === true) frame = requestAnimationFrame(() => setGone(true));
  });
  // Measured after every change of what it shows; the layout reads it.
  createEffect(() => {
    props.state();
    props.page.clock();
    if (el !== undefined) props.onMeasure?.(el.scrollHeight);
  });
  // A held or failed card shakes once.
  createEffect(
    on(
      () => props.state().kind,
      (kind) => {
        if (props.reduced || props.leaving === true || (kind !== 'held' && kind !== 'failed')) {
          return;
        }
        shake = el?.animate?.(SHAKE, { duration: 340, delay: 420, easing: 'ease-out' }) ?? null;
        shake?.finished.catch(() => {});
      },
    ),
  );
  onCleanup(() => {
    if (frame !== 0) cancelAnimationFrame(frame);
    shake?.cancel();
  });
  const transform = (): string => {
    if (entering()) return '';
    const p = props.place();
    const base = `translateY(${-(p?.y ?? 0)}px) scale(${p?.scale ?? 1})`;
    return gone() ? `${base} translateX(44px)` : base;
  };
  const kind = (): string => (props.state().kind === 'retired' ? 'moving' : props.state().kind);
  const why = () =>
    transferWhy(
      props.card,
      props.state(),
      {
        textOf: (r) => props.page.textOf(r),
        nowMs: props.page.clock(),
        nameOf: (id) => props.page.boxOf(id)?.display_name ?? id.slice(0, 8),
      },
      t,
    );
  const nameOf = (id: string): string => props.page.boxOf(id)?.display_name ?? id.slice(0, 8);
  const progress = () => transferProgress(props.state());
  const step = (): number => {
    const s = props.state();
    return s.kind === 'moving' ? s.step : 3;
  };
  const hidden = (): boolean => props.leaving === true || props.place()?.hidden === true;
  return (
    <article
      ref={el}
      class={`transfer-card transfer-card--${kind()}`}
      classList={{
        'transfer-card--behind': props.place()?.behind === true,
        'transfer-card--hidden': hidden(),
        'transfer-card--entering': entering(),
        'transfer-card--leaving': props.leaving === true,
      }}
      style={{
        transform: transform() || undefined,
        opacity: entering() ? undefined : gone() ? '0' : String(props.place()?.opacity ?? 1),
        height: props.place()?.height == null ? undefined : `${props.place()?.height}px`,
      }}
      aria-hidden={hidden() ? 'true' : undefined}
      inert={hidden() ? true : undefined}
      onClick={() => {
        if (props.place()?.behind === true) props.onBehind();
      }}
    >
      <Icon state={props.state()} />
      <div class="transfer-card__in">
        <div class="transfer-card__head">
          <b>{transferHead(props.card, props.state(), t)}</b>
          <span>{transferTag(props.state(), t)}</span>
        </div>
        <div class="transfer-card__name" title={props.card.name}>
          {props.card.name}
        </div>
        <div class="transfer-card__route">
          <span>{nameOf(props.card.fromId)}</span>
          <Show when={props.card.toId}>
            {(to) => (
              <>
                <span aria-hidden="true">→</span>
                <span>{nameOf(to())}</span>
              </>
            )}
          </Show>
        </div>
        <Show
          when={why()}
          fallback={
            <ol class="transfer-card__steps">
              <For each={STEP_KEYS}>
                {(key, i) => (
                  <li
                    classList={{
                      'transfer-card__step--done': i() < step(),
                      'transfer-card__step--cur': i() === step(),
                    }}
                    aria-current={i() === step() ? 'step' : undefined}
                  >
                    <i class="transfer-card__dot" />
                    {t(key)}
                  </li>
                )}
              </For>
            </ol>
          }
        >
          {(text) => <p class="transfer-card__why">{text()}</p>}
        </Show>
        <CardActions {...props} />
      </div>
      <button
        type="button"
        class="transfer-card__x"
        aria-label={t('fleet.transfer.dismiss')}
        onClick={(e) => {
          e.stopPropagation();
          props.onDismiss(e.currentTarget);
        }}
      >
        <svg
          width="10"
          height="10"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" />
        </svg>
      </button>
      <span
        class="transfer-card__bar"
        aria-hidden="true"
        style={{ transform: `scaleX(${progress() ?? 0})` }}
      />
    </article>
  );
}
